<script setup lang="ts">
import {
    NotificationSequence,
    NotificationSequenceTargetedConsumer
} from "@/notification-sequences/interfaces/NotificationSequence";
import useNotificationSequenceTargeting, {
    NotificationSequenceTargetingSearchParams
} from "@/notification-sequences/composables/useNotificationSequenceTargeting";
import BaseButton from "@/core/components/base/BaseButton.vue";
import dateHelper from "../../../core/helpers/dateHelper";
import {reactive, ref} from "vue";
import dayjs from "dayjs";
import BaseSelect from "@/core/components/base/BaseSelect.vue";
import ConsumerAnimationCell from "@/core/components/animation/ConsumerAnimationCell.vue";
import formatHelper from "@/core/helpers/formatHelper";
import {customerCertificationApi, notificationSequenceApi} from "@/container";
import {useToastStore} from "@/stores/toast.store";
import ConsumerAnimationTopSales from "@/core/components/animation/ConsumerAnimationTopSales.vue";

const props = defineProps<{
    sequence: NotificationSequence;
    color?: string;
}>();

const dateOptions = [
    {
        label: "Aujourd'hui",
        value: dayjs().format('YYYY-MM-DD')
    },
    {
        label: "Sur 1 mois",
        value: dayjs().subtract(1, "month").format("YYYY-MM-DD")
    },
    {
        label: "Sur 3 mois",
        value: dayjs().subtract(3, "month").format("YYYY-MM-DD")
    },
    {
        label: "Sur 6 mois",
        value: dayjs().subtract(6, "month").format("YYYY-MM-DD")
    }
];

let searchParams = reactive<NotificationSequenceTargetingSearchParams>({
    from: dateOptions[1].value,
});

const {data, isValidating, mutate} = useNotificationSequenceTargeting(props.sequence.id, searchParams);

const headers = [
    {
        title: "Patient",
        key: "consumer",
        sortable: false,
        width: "25%"
    },
    {
        title: "Ventes",
        key: "sales",
        sortable: false
    },
    {
        title: "Actions",
        key: "actions",
        sortable: false,
    }
];

const loadings = ref({});

/**
 * Include the patient in the sequence
 *
 * @param item
 */
const include = (item: NotificationSequenceTargetedConsumer) => {
    if (!formatHelper.phoneIsValid(item.customer?.information?.phone ?? '')) {

    }

    return certifyAndCreateInstance(item);
};

/**
 * Create a pre certification and include the customer in the sequence.
 * @param item
 */
const certifyAndCreateInstance = async (item: NotificationSequenceTargetedConsumer): Promise<void> => {
    loadings.value = {
        ...loadings.value,
        [item.consumer_key]: true
    };
    return certify(item)
        .then(
            () => notificationSequenceApi().createInstance(
                props.sequence.id,
                {consumer_key: item.consumer_key}
            )
                .then(() => {
                    const toast = useToastStore();
                    toast.showSuccess({content: 'La séquence a bien commencée, le premier SMS devrait partir dans les 30 minutes.'});
                    mutate();
                })
        )
        .catch(() => {
            const toast = useToastStore();
            toast.showError({content: 'Une erreur est survenue lors de la création de la séquence.'});
        })
        .finally(() => {
            loadings.value = {
                ...loadings.value,
                [item.consumer_key]: false
            };
        });
};


/**
 * Create a certification for the customer
 *
 * @param item
 */
const certify = async (item: NotificationSequenceTargetedConsumer) => {
    if (!item.customer || !formatHelper.phoneIsValid(item.customer?.information?.phone ?? '')) {
        return Promise.reject("Phone number is required");
    }

    return await customerCertificationApi()
        .certifyPhone(item.customer.id, formatHelper.apiPhone(item.customer.information.phone as string));
};

const handlePhoneChange = (value: string, item: NotificationSequenceTargetedConsumer) => {
    if (!data.value || !item.customer) {
        return;
    }

    data.value = data.value.map((instance) => {
        if (instance.customer?.information && instance.customer.id === item.customer?.id) {
            instance.customer.information.phone = value;
        }

        return instance;
    });
};
</script>

<template>
  <div class="flex flex-col">
    <div class="self-end relative">
      <BaseSelect
        v-model:model-value="searchParams.from"
        class="self-end" trigger-class="justify-end w-auto"
        :options="dateOptions"
      />
    </div>
    <v-data-table :loading="isValidating" :headers="headers" :items="data">
      <template #[`item.consumer`]="{ item }">
        <ConsumerAnimationCell
          :name="item.consumer?.information?.name ?? 'Anonyme'"
          :phone="item.customer?.information?.phone"
          :birth-date="item.consumer?.information?.birth_date"
          :editable="!!item.customer?.information"
          :onboarded="item.customer?.onboarded"
          @input="(value) => handlePhoneChange(value, item)"
        />
      </template>
      <template #[`item.sales`]="{ item }">
        <ConsumerAnimationTopSales :products="item.sales" />
      </template>
      <template #[`item.actions`]="{ item }">
        <v-tooltip v-if="!item.active_instance">
          <template #activator="{props: activatorProps}">
            <BaseButton
              v-bind="activatorProps"
              :loading="loadings[item.consumer_key]"
              density="comfortable"
              :color="formatHelper.phoneIsValid(item.customer?.information?.phone ?? '') ? color : 'grey'"
              class="text-white"
              :disabled="!formatHelper.phoneIsValid(item.customer?.information?.phone ?? '')"
              @click="include(item)"
            >
              Commencer la séquence
            </BaseButton>
          </template>
          <div class="max-w-md">
            <p>Le patient recevra le premier SMS de la séquence dans les 30 minutes</p>
          </div>
        </v-tooltip>
        <p v-else>
          Séquence commencée le {{ dateHelper.mediumFormat(item.active_instance.started_at) }}
        </p>
      </template>
    </v-data-table>
  </div>
</template>

<style scoped>

</style>
